import { API_BASE, authHeaders } from './apiAuth'

/**
 * Where a person has dragged their skill cards, one order per surface
 * (workers/api/src/routes/skillOrder.ts). The overview, the canvas rail and
 * the Miro panel each keep their own (Huanrui, 2026-10-02).
 */

/** 'quick': the skills a click of the wheel brings up on a board (QuickSkills). */
export type SkillSurface = 'home' | 'canvas' | 'panel' | 'quick' | 'quick-picture'

export async function readSkillOrder(surface: SkillSurface): Promise<string[]> {
  const response = await fetch(`${API_BASE}/api/skill-order?surface=${surface}`, {
    // No surface named for the proof: the website sends its sign-in, the
    // Miro panel its panel token -- as the pins did.
    headers: { Accept: 'application/json', ...(await authHeaders()) },
  })
  if (!response.ok) throw new Error(`Could not read the order (HTTP ${response.status})`)
  const body = (await response.json()) as { refs?: unknown }
  return Array.isArray(body.refs) ? body.refs.filter((ref): ref is string => typeof ref === 'string') : []
}

export async function saveSkillOrder(surface: SkillSurface, refs: string[]): Promise<void> {
  const response = await fetch(`${API_BASE}/api/skill-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ surface, refs }),
  })
  if (!response.ok) throw new Error(`Could not save the order (HTTP ${response.status})`)
}

/**
 * Items in a saved order: those it names, in its order, then any it does not
 * (a skill added since) in the order they came.
 */
export function arrangeBy<T extends { id: string }>(items: T[], saved: string[]): T[] {
  const at = new Map(saved.map((ref, index) => [ref, index]))
  const known = items.filter((item) => at.has(item.id)).sort((a, b) => (at.get(a.id) ?? 0) - (at.get(b.id) ?? 0))
  return [...known, ...items.filter((item) => !at.has(item.id))]
}

/**
 * A new order for part of a list, put back into the whole -- for the
 * overview, where Research or Design shows only some of the cards.
 *
 * One drag moves one card, so it is found and moved in the whole list to sit
 * just before the card it now sits before (or after the one it follows, at
 * the end). Everything else keeps its place: moving Usage to the front of
 * Research puts it at the front of All too, without shuffling Amazon.
 */
export function mergeSubset(whole: string[], before: string[], after: string[]): string[] {
  const without = (list: string[], id: string) => list.filter((one) => one !== id)
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((one, i) => one === b[i])
  const moved = after.find((id) => same(without(before, id), without(after, id)) && before.indexOf(id) !== after.indexOf(id))
  if (!moved) return whole
  const at = after.indexOf(moved)
  const rest = without(whole, moved)
  const next = after[at + 1]
  if (next !== undefined && rest.includes(next)) {
    rest.splice(rest.indexOf(next), 0, moved)
    return rest
  }
  const previous = after[at - 1]
  rest.splice(previous !== undefined ? rest.indexOf(previous) + 1 : 0, 0, moved)
  return rest
}
